import { compactFlatten, eq, object } from '@art-suite/art-core-ts';
import { getUniqueId } from '../commonLib';
import { getVersionedDataDeltaPatch, isScanDataInMemoryRoot, ScanDataInMemory } from './ScanDataInMemoryModels';

export const getScanDataInMemory = (
  scanDataInMemory: Omit<ScanDataInMemory, 'id' | 'updatedAt'> & { id?: string; updatedAt?: number },
  now = new Date(),
): ScanDataInMemory => ({
  ...(scanDataInMemory as any),
  id: getUniqueId(),
  updatedAt: scanDataInMemory.updatedAt ?? now.getTime(),
});

export const getDeletedScanDataInMemory = (s: ScanDataInMemory): ScanDataInMemory => ({
  id: getUniqueId(),
  scanPartId: s.scanPartId,
  scanTemplateId: s.scanTemplateId,
  scanId: s.scanId,
  templateScanPartId: s.templateScanPartId,
  type: s.type,

  deleted: true,
  updatedAt: Date.now(),
});

export const scanDataInMemoriesAreEqual = (before: ScanDataInMemory, after: ScanDataInMemory): boolean => {
  // expect same scanPartId
  if (before.scanPartId !== after.scanPartId) {
    throw new Error(`Scan part id expected to be the same: before: (id: ${before.scanPartId}), after: (id: ${after.scanPartId})`);
  }
  // check fields that should never change for the same scanPartId
  if (before.type !== after.type) throw new Error(`Scan part type expected to be the same: before: (type: ${before.type}), after: (type: ${after.type})`);
  if (before.scanId !== after.scanId) throw new Error(`Scan id expected to be the same: before: (id: ${before.scanId}), after: (id: ${after.scanId})`);
  if (before.scanTemplateId !== after.scanTemplateId) {
    throw new Error(`Scan template id expected to be the same: before: (id: ${before.scanTemplateId}), after: (id: ${after.scanTemplateId})`);
  }
  if (before.templateScanPartId !== after.templateScanPartId) {
    throw new Error(`Template scan part id expected to be the same: before: (id: ${before.templateScanPartId}), after: (id: ${after.templateScanPartId})`);
  }
  return eq(before.data, after.data) && eq(before.childrenScanPartIds, after.childrenScanPartIds);
};

/**
 * Pure function: Get deleted, added and changed ScanData records comparing before and after ScanRoots
 * before and after MUST be full sets of save data - any missing scanPartIds will be treated as delete
 * @param scanBefore
 * @param scanAfter
 * @returns
 */
export const getScanDataInMemoryDelta = (
  allScanDataInMemoryBefore: ScanDataInMemory[],
  allScanDataInMemoryAfter: ScanDataInMemory[],
  now = new Date(),
): ScanDataInMemory[] => {
  const scanBeforeAllScanDataInMemoryByScanPartId = object(allScanDataInMemoryBefore, { withKey: ({ scanPartId }) => scanPartId });
  const scanAfterAllScanDataInMemoryByScanPartId = object(allScanDataInMemoryAfter, { withKey: ({ scanPartId }) => scanPartId });

  const deletedScanDataInMemory: ScanDataInMemory[] = allScanDataInMemoryBefore.filter(scanDataInMemory =>
    !scanAfterAllScanDataInMemoryByScanPartId[scanDataInMemory.scanPartId]
  );
  const newOrChangedScanDataInMemory: (ScanDataInMemory | undefined)[] = allScanDataInMemoryAfter.map(after => {
    const before = scanBeforeAllScanDataInMemoryByScanPartId[after.scanPartId];

    // added
    if (!before) {
      if (isScanDataInMemoryRoot(after)) {
        console.error("BUG: can't add a new root!", {
          newRoot: after,
          existingRoot: allScanDataInMemoryBefore.filter(isScanDataInMemoryRoot),
          data: { allScanDataInMemoryBefore, allScanDataInMemoryAfter },
        });
      }
      return after;
    }

    // no change
    if (scanDataInMemoriesAreEqual(before, after)) return undefined;

    // changed
    return getScanDataInMemory({
      ...after,
      basedOnLastUpdatedAt: before.updatedAt,
      dataDeltaPatch: getVersionedDataDeltaPatch(before, after),
    }, now);
  });

  return compactFlatten([
    deletedScanDataInMemory.map(getDeletedScanDataInMemory),
    newOrChangedScanDataInMemory,
  ]);
};
